import { isDefined, isEmpty } from "@clipboard-health/util-ts";
import { Alert, Autocomplete, CircularProgress, Stack, TextField } from "@mui/material";
import { useGetFacilityProfile } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { useDebounce } from "@src/appV2/lib/utils";
import { formatCbhApiError, isCbhApiAxiosError } from "@src/appV2/lib/utils/errorUtils";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { ResponsiveDialog } from "@src/appV2/redesign/components/ResponsiveDialog";
import { Text } from "@src/appV2/redesign/components/Text";
import { useCopyExclusions } from "@src/appV2/redesign/DNR/api/useCopyExclusions";
import { useGetExclusionsMeta } from "@src/appV2/redesign/DNR/api/useGetExclusionsMeta";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { type Facility, useSearchFacilities } from "@src/appV2/User/api/useSearchFacilities";
import { type UseModalState } from "@src/lib/ui-react";
import { useEffect, useRef, useState } from "react";

export interface CopyDnrListOriginWorkplace {
  id: string;
  name: string;
  address?: string;
}

interface DestinationWorkplace {
  id: string;
  name: string;
}

export interface CopyDnrListDialogProps {
  modalState: UseModalState;
  origin: CopyDnrListOriginWorkplace;
  /**
   * Workplace `userId` to pre-select as the destination (e.g. the source's
   * `changeOfOwnership.replacementFacilityId`). Its name is resolved for display;
   * the admin can still change the selection.
   */
  defaultDestinationWorkplaceId?: string;
}

type DialogStep = "form" | "success";

interface CopyResult {
  copiedCount: number;
  skippedCount: number;
}

/** Extracts a user-facing message from a copy failure, falling back to a generic message. */
function getErrorMessage(error: unknown): string {
  if (isCbhApiAxiosError(error)) {
    const firstError = error.response.data.errors[0];
    if (isDefined(firstError)) {
      const formatted = formatCbhApiError(firstError);
      if (formatted.length > 0) {
        return formatted;
      }
    }
  }
  return "Failed to copy DNR list. Please try again or contact support.";
}

/** Returns the destination autocomplete's empty-state text for the current search/loading state. */
function getNoOptionsText(searchInput: string, isLoading: boolean): string {
  if (isEmpty(searchInput)) {
    return "Start typing to search for workplaces";
  }
  return isLoading ? "Searching workplaces..." : "No workplaces found";
}

/** Copy button label reflecting the source's DNR count, mirroring `TransferDnrDialog`. */
function getCopyButtonLabel(params: {
  isCopying: boolean;
  isMetaLoading: boolean;
  dnrCount: number | undefined;
}): string {
  const { isCopying, isMetaLoading, dnrCount } = params;
  if (isCopying) {
    return "Copying...";
  }
  if (isMetaLoading) {
    return "Loading DNR count...";
  }
  if (!isDefined(dnrCount)) {
    return "Unable to load DNR count";
  }
  return dnrCount === 1 ? "Copy 1 DNR" : `Copy ${dnrCount} DNRs`;
}

/**
 * Modal for copying the source workplace's DNR list (exclusions) into a destination
 * workplace. The admin searches for and selects the destination (optionally pre-filled
 * from the change-of-ownership replacement), sees how many DNRs will be copied, confirms,
 * and sees a success summary. Errors are surfaced inline.
 *
 * Reuses the backend `/exclusion/copy` API (via `useCopyExclusions`) and the source DNR
 * count (via `useGetExclusionsMeta`), but keeps a single-source, CHOW-styled UI rather than
 * the dual-selector `TransferDnrDialog`.
 */
export function CopyDnrListDialog(props: CopyDnrListDialogProps) {
  const { modalState, origin, defaultDestinationWorkplaceId } = props;

  const [step, setStep] = useState<DialogStep>("form");
  const [destination, setDestination] = useState<DestinationWorkplace | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [copyResult, setCopyResult] = useState<CopyResult | undefined>(undefined);
  const [didPrefillDestination, setDidPrefillDestination] = useState(false);
  // Sticky flag mirroring `CloneNfcTagsDialog`: any destination interaction (selecting,
  // clearing, or typing a search) before the replacement lookup resolves permanently
  // disables the prefill, so a late-resolving lookup can't overwrite an in-progress search
  // the admin already started.
  const didUserEditDestinationRef = useRef(false);

  const debouncedSearch = useDebounce({ debounceTimeInMs: 300, value: searchInput });

  // The DNR count is keyed off the fixed source workplace, so it loads as soon as the
  // dialog opens and drives the "Copy N DNRs" label + the zero-DNR disable.
  const { data: sourceMeta, isLoading: isMetaLoading } = useGetExclusionsMeta({
    facilityId: origin.id,
  });
  const dnrCount = sourceMeta?.count;

  const { mutateAsync: copyExclusions, isLoading: isCopying } = useCopyExclusions();

  // Resolve the change-of-ownership replacement workplace so we can show its name in the
  // selector. It is a workplace `userId`, the same id the copy endpoint uses. Never prefill
  // the source itself, which the copy API would reject.
  const shouldPrefillDestination =
    isDefined(defaultDestinationWorkplaceId) &&
    defaultDestinationWorkplaceId.length > 0 &&
    defaultDestinationWorkplaceId !== origin.id;

  const { data: defaultDestinationProfile } = useGetFacilityProfile(
    defaultDestinationWorkplaceId ?? "",
    // Space-separated: the facility-profile API splits projections on spaces, so a comma-joined
    // value is treated as one (forbidden) field and 400s.
    "name userId",
    { enabled: shouldPrefillDestination }
  );

  // Pre-populate the selector once from the change-of-ownership field. The admin can still
  // clear or change it afterwards, so we never override a later edit.
  useEffect(() => {
    if (didPrefillDestination || !isDefined(defaultDestinationProfile)) {
      return;
    }
    setDidPrefillDestination(true);
    if (didUserEditDestinationRef.current || isDefined(destination)) {
      return;
    }
    setDestination({
      id: defaultDestinationProfile.userId,
      name: defaultDestinationProfile.name,
    });
  }, [didPrefillDestination, destination, defaultDestinationProfile]);

  const { data: facilitiesData, isFetching: facilitiesLoading } = useSearchFacilities(
    { searchInput: debouncedSearch },
    { enabled: debouncedSearch.length > 0 }
  );

  const destinationOptions: DestinationWorkplace[] =
    facilitiesData?.pages
      .flatMap((page) => page.list)
      .filter((facility: Facility) => facility.userId !== origin.id)
      .map((facility: Facility) => ({ id: facility.userId, name: facility.name })) ?? [];

  const resetAndClose = () => {
    setStep("form");
    setDestination(null);
    setSearchInput("");
    setErrorMessage(undefined);
    setCopyResult(undefined);
    setDidPrefillDestination(false);
    didUserEditDestinationRef.current = false;
    modalState.closeModal();
  };

  const handleCopy = async () => {
    if (!isDefined(destination)) {
      return;
    }
    setErrorMessage(undefined);
    try {
      const response = await copyExclusions({
        sourceWorkplaceId: origin.id,
        targetWorkplaceId: destination.id,
      });
      setCopyResult({ copiedCount: response.copiedCount, skippedCount: response.skippedCount });
      setStep("success");
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  const isCopyDisabled =
    !isDefined(destination) || isMetaLoading || isCopying || !isDefined(dnrCount) || dnrCount === 0;

  let title: string;
  let body: React.ReactNode;
  let footer: React.ReactNode;

  if (step === "success") {
    const copiedCount = copyResult?.copiedCount ?? 0;
    const skippedCount = copyResult?.skippedCount ?? 0;
    title = "DNR list copied";
    body = (
      <Text variant="body2">
        Copied {copiedCount} DNR{copiedCount === 1 ? "" : "s"} from {origin.name} into{" "}
        {destination?.name ?? "the destination workplace"}.
        {skippedCount > 0
          ? ` ${skippedCount} worker${
              skippedCount === 1 ? " was" : "s were"
            } already DNR'd at the destination and ${skippedCount === 1 ? "was" : "were"} skipped.`
          : ""}
      </Text>
    );
    footer = (
      <DialogFooter
        primaryAction={{
          label: "Close",
          onClick: resetAndClose,
        }}
      />
    );
  } else {
    title = "Copy DNR list";
    body = (
      <Stack spacing={4}>
        <Stack spacing={1}>
          <Text semibold variant="body2">
            Source workplace
          </Text>
          <Text variant="body2">{origin.name}</Text>
          <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant="caption">
            {origin.address ?? "Address unavailable"}
          </Text>
        </Stack>

        <Stack spacing={1}>
          <Text semibold variant="body2">
            Destination workplace
          </Text>
          <Autocomplete
            autoComplete
            disabled={isCopying}
            filterOptions={(options) => options}
            getOptionKey={(option) => (typeof option === "string" ? option : option.id)}
            getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
            isOptionEqualToValue={(option, value) => {
              if (typeof option === "string" || typeof value === "string") {
                return false;
              }
              return option.id === value.id;
            }}
            loading={facilitiesLoading}
            loadingText="Searching workplaces..."
            noOptionsText={getNoOptionsText(searchInput, facilitiesLoading)}
            options={destinationOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Start typing workplace name..."
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {facilitiesLoading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(renderProps, option) => {
              if (!option) {
                return null;
              }
              const { key: _key, ...liProps } =
                renderProps as React.HTMLAttributes<HTMLLIElement> & {
                  key?: React.Key;
                };
              return (
                <li key={option.id} {...liProps}>
                  <Stack direction="column">
                    <Text variant="body2">{option.name}</Text>
                    <Text
                      sx={(theme) => ({ color: theme.palette.text.secondary })}
                      variant="caption"
                    >
                      ID: {option.id}
                    </Text>
                  </Stack>
                </li>
              );
            }}
            value={destination}
            onChange={(_, newValue) => {
              didUserEditDestinationRef.current = true;
              if (typeof newValue !== "string") {
                setDestination(newValue);
              }
            }}
            onInputChange={(_, newInputValue, reason) => {
              // Only typed input counts as a user edit; "reset" also fires when the
              // prefill itself populates the field.
              if (reason === "input") {
                didUserEditDestinationRef.current = true;
              }
              setSearchInput(newInputValue);
            }}
          />
        </Stack>

        <Alert severity="info">
          This copies every DNR from {origin.name} into the destination workplace. Workers already
          DNR&apos;d at the destination are skipped.
        </Alert>

        {isDefined(dnrCount) && dnrCount === 0 && !isMetaLoading && (
          <Alert severity="warning">{origin.name} has no DNRs to copy.</Alert>
        )}

        {isDefined(errorMessage) && <Alert severity="error">{errorMessage}</Alert>}
      </Stack>
    );
    footer = (
      <DialogFooter
        primaryAction={{
          label: getCopyButtonLabel({ isCopying, isMetaLoading, dnrCount }),
          isLoading: isCopying,
          disabled: isCopyDisabled,
          onClick: () => {
            void handleCopy();
          },
        }}
        secondaryAction={{
          label: "Cancel",
          disabled: isCopying,
          onClick: resetAndClose,
        }}
      />
    );
  }

  return (
    <BerlinThemeProvider>
      <ResponsiveDialog
        showCloseButton
        footerContent={footer}
        modalState={modalState}
        title={title}
        width={520}
        onClose={resetAndClose}
      >
        {body}
      </ResponsiveDialog>
    </BerlinThemeProvider>
  );
}
